import React, { useState, useContext } from 'react';
import { Col, Table, Row, Form } from 'reactstrap';
import { HiPencil } from 'react-icons/hi';
import { IoIosAdd } from 'react-icons/io';
import { MdDelete } from 'react-icons/md';
import { MenuItem } from '@mui/material';
import { confirmAlert } from 'react-confirm-alert';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import './style.scss';
import AlertComponent from 'components/Alert/index.js';
import ButtonComponent from './../../../../components/Buttons';
import InputField from 'components/Input/InputField.js';
import Loading from 'components/Loading';
import ModalComponent from 'components/Modal/index.js';
import SearchComponent from './../../../../components/Search';
import SelectComponent from 'components/Select/Select.js';
import Sidebar from './../Sidebar.js';
import {
  HTTP_UNPROCESSABLE_ENTITY,
  HTTP_OK,
  HTTP_NOT_ACCESS,
  HTTP_INTERNAL_SERVER_ERROR,
  HTTP_EXPECTATION_FAILED,
} from 'constants/StatusCode.js';
import { MAX_LENGTH_20 } from 'constants/ProfileEmployee.js';
import { USE_LIST_SKILL } from 'constants/MasterData.js';
import { apiCreateSkill, apiUpdateSkill, apiDeleteSkill } from 'api/masterData.js';
import { isNotNullObject } from 'helpers/index.js';
import { useGetSkill, useGetPosition } from 'hook/useMasterData.js';
import { ThemContext } from 'layouts/Admin';
import { ROLE_VIEW, DISABLE_ROLE, DISABLE_BUTTON_ROLE } from 'constants/index.js';

const DEFAULT_FORM_DATA = {
  id: null,
  name: '',
  position_id: 0,
  error_skill: '',
  error_position: '',
};

const DEFAULT_FORM_DATA_ERROR = {
  error_skill: '',
  error_position: '',
};

const Skill = () => {
  let { t } = useTranslation();
  const queryClient = useQueryClient();
  const dataInforUser = useContext(ThemContext);
  const [isShowModal, setShowModal] = useState(false);
  const [key_word, setKeyWord] = useState('');
  const [position_id, setPositionId] = useState(0);
  const [stateButton, setStateButton] = useState(false);
  const [formAdd, setFormAdd] = useState(DEFAULT_FORM_DATA);
  const [isShowAlert, setShowAlert] = useState(false);
  const [message, setMessage] = useState('');
  const [textModal, setTextModal] = useState({
    title: t('masterData.moreSkill'),
    btn_submit: t('common.add'),
  });

  const handleSearch = (value) => {
    if (value.length > MAX_LENGTH_20) {
      return;
    }
    setKeyWord(value);
  };

  const handleSelectSearch = (event) => {
    setPositionId(event.target.value);
  };

  const addSkill = () => {
    if (dataInforUser?.hr_permission?.master_data_permission === ROLE_VIEW) {
      return;
    }
    setShowModal(true);
    setFormAdd({ ...formAdd, ...DEFAULT_FORM_DATA });
    setTextModal({
      title: t('masterData.moreSkill'),
      btn_submit: t('common.add'),
    });
  };

  function onclickEdit(idx) {
    if (dataInforUser?.hr_permission?.master_data_permission === ROLE_VIEW) {
      return;
    }
    setShowModal(true);
    setTextModal({
      title: t('masterData.updatingSkill'),
      btn_submit: t('common.update'),
    });
    setFormAdd({
      ...formAdd,
      id: idx.id,
      name: idx.name,
      position_id: idx.position_id,
      ...DEFAULT_FORM_DATA_ERROR,
    });
  }

  const handleInput = (e) => {
    setFormAdd({ ...formAdd, [e.target.name]: e.target.value, error_skill: '' });
  };

  const handleSelect = (event) => {
    setFormAdd({ ...formAdd, position_id: event.target.value, error_position: '' });
  };

  const { data: uListSkill, isLoading: isLoadingListSkill } = useGetSkill({ key_word, position_id });

  const { data: uListPosition } = useGetPosition();

  const alertConfirm = (id) => {
    if (dataInforUser?.hr_permission?.master_data_permission === ROLE_VIEW) {
      return;
    }
    confirmAlert({
      title: t('infoEmployee.youWantToDelete'),
      buttons: [
        {
          label: t('infoEmployee.cancel'),
          className: 'btn-alert-cancel',
        },
        {
          label: t('infoEmployee.ok'),
          className: 'btn-alert-ok',
          onClick: async () => {
            const response = await apiDeleteSkill(id);
            if (response?.data.status === HTTP_OK) {
              setMessage(response.data.messages);
              setShowAlert(true);
              queryClient.invalidateQueries(USE_LIST_SKILL);
            }
            if (response?.data?.status === HTTP_NOT_ACCESS && response?.data?.is_edit) {
              setMessage(t('common.pleaseCheckAgainYouAreNotAuthorizedToUseThisFeature'));
              setShowAlert(true);
            }
            if (
              response?.data.status === HTTP_INTERNAL_SERVER_ERROR ||
              response?.data.status === HTTP_EXPECTATION_FAILED
            ) {
              setMessage(response?.data?.messages);
              setShowAlert(true);
              queryClient.invalidateQueries(USE_LIST_SKILL);
            }
          },
        },
      ],
    });
  };

  function validate() {
    var messages = {};
    if (!formAdd.name) {
      messages.error_skill = t('common.pleaseFillOutThisField');
    }
    if (formAdd.name.length > MAX_LENGTH_20) {
      messages.error_skill = t('common.pleaseEnterLessThanTwentyCharacters');
    }
    if (!formAdd.position_id) {
      messages.error_position = t('common.pleaseSelectField');
    }
    if (isNotNullObject(messages)) {
      setFormAdd({
        ...formAdd,
        ...messages,
      });
      return false;
    }
    return true;
  }

  const handleSubmit = async (event) => {
    setFormAdd({ ...formAdd, ...DEFAULT_FORM_DATA_ERROR });
    setStateButton(true);
    event.preventDefault();

    if (!validate()) {
      setStateButton(false);
      return;
    }

    setFormAdd({ ...formAdd, ...DEFAULT_FORM_DATA_ERROR });

    const data = {
      name: formAdd.name.trim(),
      position_id: formAdd.position_id,
    };

    let response = {};
    if (!formAdd.id) {
      response = await apiCreateSkill(data);
    } else {
      response = await apiUpdateSkill(formAdd.id, data);
    }

    if (response?.data?.status === HTTP_UNPROCESSABLE_ENTITY && response?.data?.messages) {
      const msg = response.data.messages;
      setFormAdd({
        ...formAdd,
        error_skill: msg?.name
          ?.map((item) => {
            return item;
          })
          .join('\n'),
        error_position: msg?.position_id
          ?.map((item) => {
            return item;
          })
          .join('\n'),
      });

      setStateButton(false);

      return;
    }
    if (response?.data?.status === HTTP_NOT_ACCESS && response?.data?.is_edit) {
      setMessage(t('common.pleaseCheckAgainYouAreNotAuthorizedToUseThisFeature'));
      setShowAlert(true);
      setStateButton(false);

      return;
    }
    if (response?.data?.status === HTTP_OK) {
      setShowModal(false);
      setFormAdd({ ...formAdd, ...DEFAULT_FORM_DATA });
      setMessage(response.data.messages);
      setShowAlert(true);
      setStateButton(false);

      queryClient.invalidateQueries(USE_LIST_SKILL);
    }
    setTextModal({
      ...textModal,
      title: t('masterData.moreSkill'),
      btn_submit: t('common.add'),
    });
    setStateButton(false);
  };

  return (
    <div className="master-data-skill">
      <Row>
        <Col lg={3} xl={2}>
          <Sidebar />
        </Col>
        <Col lg={9} xl={10}>
          <Row>
            <Col className="md-12">
              <div className="box-group d-flex float-right">
                <SearchComponent
                  txtPlaceholder={t('masterData.findData')}
                  addClass="input-search"
                  handleSearch={handleSearch}
                  maxLength={MAX_LENGTH_20}
                  defaultValue={key_word}
                />
                <div className="dropBox ml-3">
                  <SelectComponent
                    addClass="select-position"
                    name="position_id"
                    valueDefault={position_id}
                    onHandleChangeSelect={handleSelectSearch}
                    sizeSelect="small"
                  >
                    <MenuItem value={0}>{t('employees.position')}</MenuItem>
                    {uListPosition?.data &&
                      uListPosition?.data?.map((item, index) => {
                        return (
                          <MenuItem key={index} value={item.id}>
                            {item.name}
                          </MenuItem>
                        );
                      })}
                  </SelectComponent>
                </div>
                <ButtonComponent
                  text={t('common.add')}
                  icon={<IoIosAdd />}
                  addClass={`${
                    dataInforUser?.hr_permission?.master_data_permission === ROLE_VIEW && DISABLE_BUTTON_ROLE
                  } ml-3`}
                  className="button-add"
                  handleClick={() => addSkill()}
                />
              </div>
            </Col>
          </Row>
          <Row className="mt-3">
            <Col className="md-12">
              <div className="table-list">
                <Table className="align-items-center table-hover" responsive>
                  <thead>
                    <tr>
                      <th scope="col">{t('masterData.skillCode')}</th>
                      <th scope="col">{t('masterData.skillName')}</th>
                      <th scope="col">{t('employees.position')}</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {uListSkill?.data?.length === 0 ? (
                      <tr>
                        <td colSpan={4}>
                          <div className="error-no-data">{t('common.notData')}</div>
                        </td>
                      </tr>
                    ) : (
                      uListSkill?.data?.map((val, index) => (
                        <tr key={index}>
                          <td>{val.id}</td>
                          <td className="text-bold">{val.name}</td>
                          <td>{val.position_name}</td>
                          <td>
                            <div className="col-action d-flex float-right">
                              <div
                                className={`${
                                  dataInforUser?.hr_permission?.master_data_permission === ROLE_VIEW && DISABLE_ROLE
                                } edit mr-2`}
                                onClick={() => onclickEdit(val)}
                              >
                                <HiPencil />
                              </div>
                              <div
                                className={`${
                                  dataInforUser?.hr_permission?.master_data_permission === ROLE_VIEW && DISABLE_ROLE
                                } remove mr-3`}
                                onClick={() => alertConfirm(val.id)}
                              >
                                <MdDelete />
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                    {isLoadingListSkill && (
                      <tr>
                        <td colSpan={4}>
                          <Loading addClass="mt-2" />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>

      <ModalComponent toggle={() => setShowModal(!isShowModal)} isShowModal={isShowModal} title={textModal.title}>
        <Form className="mt-3" onSubmit={handleSubmit}>
          <div className="fieldset-group">
            <SelectComponent
              name="position_id"
              addClass="select"
              valueDefault={formAdd.position_id}
              onHandleChangeSelect={handleSelect}
              error={formAdd.error_position}
            >
              <MenuItem value={0}>{t('employees.position')}</MenuItem>
              {uListPosition?.data &&
                uListPosition?.data?.map((item, index) => {
                  return (
                    <MenuItem key={index} value={item.id}>
                      {item.name}
                    </MenuItem>
                  );
                })}
            </SelectComponent>
          </div>

          <div className="fieldset-group mt-4 ">
            <InputField
              label={t('masterData.skillName')}
              variant="outlined"
              name="name"
              maxLength={MAX_LENGTH_20}
              defaultValue={formAdd.name}
              onHandleInput={handleInput}
              error={formAdd.error_skill}
              isRequired={true}
            />
          </div>

          <Row className="mt-5">
            <Col className="md-6">
              <ButtonComponent
                text={t('common.cancel')}
                addClass="w-100 btn-bg-yellow2"
                handleClick={() => setShowModal(false)}
              />
            </Col>
            <Col className="md-6">
              <ButtonComponent
                text={textModal.btn_submit}
                addClass="w-100"
                handleClick={() => setShowModal(true)}
                type="submit"
                hasDisabled={stateButton}
                isLoading={stateButton}
              />
            </Col>
          </Row>
        </Form>
      </ModalComponent>
      <AlertComponent toggle={() => setShowAlert(!isShowAlert)} isShowAlert={isShowAlert}>
        {message}
      </AlertComponent>
    </div>
  );
};

export default Skill;
